import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { getTrend, getTrendColor } from './stat-card-utils'

/**
 * NPS円グラフカードコンポーネント
 * NPSスコア、Promoters、Passives、Detractorsの内訳、前月比を表示
 */
type Props = {
  npsScore: number
  breakdown: {
    promoters: number
    passives: number
    detractors: number
    promotersPct: number
    passivesPct: number
    detractorsPct: number
  }
  prevNpsChange?: number
  prevNpsScore?: number
}

export const NpsGaugeCard: React.FC<Props> = ({ npsScore, breakdown, prevNpsChange, prevNpsScore }) => {
  const { t } = useTranslation()
  const data = [
    { name: 'Promoters', value: breakdown.promotersPct, color: '#4CAF50' },
    { name: 'Passives', value: breakdown.passivesPct, color: '#FFC107' },
    { name: 'Detractors', value: breakdown.detractorsPct, color: '#F44336' },
  ]

  const trend = useMemo(() => getTrend(prevNpsChange), [prevNpsChange])
  const trendColor = useMemo(() => getTrendColor(trend), [trend])
  const trendColorStyle = useMemo(() => css({ color: trendColor }), [trendColor])

  return (
    <div css={containerStyle}>
      <div css={headerRowStyle}>
        <div css={gaugeContainerStyle}>
          <div css={scoreContainerStyle}>
            <div css={scoreStyle}>{npsScore}</div>
            <div css={scoreLabelStyle}>NPS</div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart margin={{ top: 0, right: 0, bottom: 20, left: 0 }}>
              <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={60} startAngle={90} endAngle={-270} dataKey="value">
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        {prevNpsChange !== undefined && (
          <div css={[comparisonStyle, trendColorStyle]}>
            <div css={arrowStyle}>{trend === 'up' ? '⬆︎' : trend === 'down' ? '⬇︎' : ''}</div>
            <div css={comparisonTextStyle}>
              <div>{t('PrevMonthComparison', { rate: '' }).trim()}</div>
              {prevNpsScore !== undefined ? (
                <div css={prevValueStyle}>
                  {prevNpsChange}({prevNpsScore})
                </div>
              ) : (
                <div css={prevValueStyle}>{prevNpsChange}</div>
              )}
            </div>
          </div>
        )}
      </div>
      <table css={legendTableStyle}>
        <tbody>
          <tr css={legendRowStyle}>
            <td css={legendColorCellStyle}>
              <div css={[legendColorStyle, { background: '#4CAF50' }]} />
            </td>
            <td css={[legendLabelCellStyle, { color: '#4CAF50' }]}>Promoters</td>
            <td css={[legendCountCellStyle, { color: '#4CAF50' }]}>
              {breakdown.promoters}
              {t('Person')}
            </td>
            <td css={legendPercentCellStyle}>{breakdown.promotersPct}%</td>
          </tr>
          <tr css={legendRowStyle}>
            <td css={legendColorCellStyle}>
              <div css={[legendColorStyle, { background: '#FFC107' }]} />
            </td>
            <td css={[legendLabelCellStyle, { color: '#FFC107' }]}>Passives</td>
            <td css={[legendCountCellStyle, { color: '#FFC107' }]}>
              {breakdown.passives}
              {t('Person')}
            </td>
            <td css={legendPercentCellStyle}>{breakdown.passivesPct}%</td>
          </tr>
          <tr css={legendRowStyle}>
            <td css={legendColorCellStyle}>
              <div css={[legendColorStyle, { background: '#F44336' }]} />
            </td>
            <td css={[legendLabelCellStyle, { color: '#F44336' }]}>Detractors</td>
            <td css={[legendCountCellStyle, { color: '#F44336' }]}>
              {breakdown.detractors}
              {t('Person')}
            </td>
            <td css={legendPercentCellStyle}>{breakdown.detractorsPct}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '6px',
  width: '100%',
  height: '100%',
})

const headerRowStyle = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  marginBottom: '16px',
  minWidth: 0,
})

const gaugeContainerStyle = css({
  position: 'relative',
  flex: '1 1 50%',
  minWidth: '120px',
  maxWidth: '200px',
  width: '100%',
  flexShrink: 1,
})

const scoreContainerStyle = css({
  position: 'absolute',
  top: '45%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  zIndex: 1,
})

const scoreStyle = css({
  fontSize: '40px',
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: 1,
})

const scoreLabelStyle = css({
  fontSize: '20px',
  color: '#676767',
  marginTop: '4px',
})

const comparisonStyle = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: '0 1 auto',
  minWidth: '60px',
  gap: '4px',
  flexShrink: 1,
})

const arrowStyle = css({
  fontSize: '24px',
  fontWeight: 'bold',
})

const comparisonTextStyle = css({
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
})

const prevValueStyle = css({
  fontSize: '12px',
})

const legendTableStyle = css({
  width: '100%',
  borderCollapse: 'collapse',
  borderSpacing: 0,
})

const legendRowStyle = css({
  fontSize: '14px',
  color: '#272727',
})

const legendColorCellStyle = css({
  padding: '4px 8px 4px 0',
  verticalAlign: 'middle',
  width: '20px',
})

const legendLabelCellStyle = css({
  padding: '4px 8px 4px 0',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
})

const legendCountCellStyle = css({
  padding: '4px 8px 4px 0',
  verticalAlign: 'middle',
  textAlign: 'right',
  whiteSpace: 'nowrap',
})

const legendPercentCellStyle = css({
  padding: '4px 0',
  verticalAlign: 'middle',
  textAlign: 'right',
  whiteSpace: 'nowrap',
})

const legendColorStyle = css({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
})
